import { MapSitemapConfig } from "@iventis/domain-model/model/mapSitemapConfig";
import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { CompositionMapObject } from "@iventis/map-engine";
import { ObjectUpdate } from "@iventis/map-engine/src/types/events-store";
import { PostImportedGeoJsonMapObject } from "@iventis/map-export-import-mapper";
import { getMapObjectSitemap } from "@iventis/map-objects";
import { findNode, findNodes, getNodeNestedLevel, getNodesWithoutParent } from "@iventis/tree-browser";
import { canEdit, canEditAll, isAdmin, isAdminOnAll } from "@iventis/utilities";
import { DomainLayer } from "@iventis/map-engine/src/state/map.state.types";
import { CopyActionValidationStatus, DeleteActionValidationStatus, GroupActionValidationStatus, UnlinkLayerValidationStatus } from "./sidebar-toolbar-types";

/**
 * Checks if the selected layer and folders can be grouped
 * This assumes when a folder is selected all of the children are also selected
 */
export function canNodesBeGrouped(selectedNodeIds: string[], mapNode: Node): GroupActionValidationStatus {
    // If user doesn't have admin on the plan they can't group anything
    if (!isAdmin(mapNode.authorisation)) {
        return GroupActionValidationStatus.InvalidPermissions;
    }

    if (selectedNodeIds.length < 1) {
        return GroupActionValidationStatus.InvalidSelectionAmount;
    }

    // Find all nodes
    const selectedNodes = selectedNodeIds.flatMap((nodeId) => {
        const node = findNode(mapNode.childNodes, nodeId);
        return node != null ? [node] : [];
    });

    // Check each node is a Layer or Folder
    if (!selectedNodes.every((node) => node.type === NodeType.Layer || node.type === NodeType.Folder)) {
        return GroupActionValidationStatus.InvalidTypeSelection;
    }

    // Check each node is not already nested 3 nodes deep
    if (
        !selectedNodes.every((node) => {
            const level = getNodeNestedLevel(mapNode, node);
            // If a group is empty it won't have any layers and therefore we need to reduce the nested level check by 1
            return node.type === NodeType.Folder ? level < 2 : level < 3;
        })
    ) {
        return GroupActionValidationStatus.InvalidNestedLevel;
    }

    // Get nodes that don't have their parent also selected
    // These nodes will potentially become direct children of the new group
    const nodesWithoutParents = getNodesWithoutParent(selectedNodes);

    if (nodesWithoutParents.length === 0) {
        return GroupActionValidationStatus.InvalidParent;
    }

    // Check every nodeWithoutParent has same parentId, if they do then they can be grouped
    const singleParentId = nodesWithoutParents[0].parentId;
    if (nodesWithoutParents.some((node) => node.parentId !== singleParentId)) {
        return GroupActionValidationStatus.InvalidParent;
    }

    return GroupActionValidationStatus.Valid;
}

export function canNodesBeUnlinked(selectedLayers: DomainLayer[], selectedNodeIds: string[], mapNode: Node) {
    // If user doesn't have write on the plan they can't unlink anything
    if (!canEdit(mapNode.authorisation)) {
        return UnlinkLayerValidationStatus.InvalidPermissions;
    }

    if (selectedLayers.length < 1) {
        return UnlinkLayerValidationStatus.InvalidSelectionAmount;
    }

    // Each selected layer needs to be imported
    if (selectedLayers.some((layer) => !layer.imported)) {
        return UnlinkLayerValidationStatus.InvalidSelectionNonImport;
    }

    // Get all selected nodes
    const selectedNodes = selectedNodeIds.flatMap((nodeId) => {
        const node = findNode(mapNode.childNodes, nodeId);
        return node != null ? [node] : [];
    });

    // Each selected node needs to be of type layer
    if (selectedNodes.some((node) => node.type !== NodeType.Layer && node.type !== NodeType.Folder)) {
        return UnlinkLayerValidationStatus.InvalidTypeSelection;
    }

    return UnlinkLayerValidationStatus.Valid;
}

export function canNodesBeDeleted(selectedNodeIds: string[], mapNode: Node) {
    if (selectedNodeIds.length < 1) {
        return DeleteActionValidationStatus.InvalidSelectionAmount;
    }

    // Get all selected nodes
    const selectedNodes = selectedNodeIds.flatMap((nodeId) => {
        const node = findNode(mapNode.childNodes, nodeId);
        return node != null ? [node] : [];
    });

    // Each selected node needs to be of type layer or folder
    if (selectedNodes.some((node) => node.type !== NodeType.Layer && node.type !== NodeType.Folder)) {
        return DeleteActionValidationStatus.InvalidTypeSelection;
    }

    const { linkedLayerNodes, otherSelectedNodes } = selectedNodes.reduce(
        (selectedNodeGroups, node) => {
            const isLinkedLayer = node.type === NodeType.Layer && node.sourceId !== node.id;

            if (isLinkedLayer) {
                selectedNodeGroups.linkedLayerNodes.push(node);
            } else {
                selectedNodeGroups.otherSelectedNodes.push(node);
            }

            return selectedNodeGroups;
        },
        { linkedLayerNodes: [] as Node[], otherSelectedNodes: [] as Node[] }
    );

    // Linked layer nodes require map-level admin
    if (linkedLayerNodes.length > 0 && !isAdmin(mapNode.authorisation)) {
        return DeleteActionValidationStatus.InvalidPermissions;
    }

    // Non-linked nodes require node-level admin
    if (otherSelectedNodes.length > 0 && !isAdminOnAll(otherSelectedNodes)) {
        return DeleteActionValidationStatus.InvalidPermissions;
    }

    return DeleteActionValidationStatus.Valid;
}

export function canNodesBeCopied(selectedNodeIds: string[], mapNode: Node) {
    // If user doesn't have write on the plan they can't copy anything
    if (!canEdit(mapNode.authorisation)) {
        return CopyActionValidationStatus.InvalidPermissions;
    }

    // Get all selected nodes
    const selectedNodes = selectedNodeIds.flatMap((nodeId) => {
        const node = findNode(mapNode.childNodes, nodeId);
        return node != null ? [node] : [];
    });

    if (selectedNodes.length < 1 || !selectedNodes.some((node) => node.type === NodeType.Layer)) {
        return CopyActionValidationStatus.InvalidSelectionAmount;
    }

    // Each selected node needs to be of type layer or group
    if (!selectedNodes.every((node) => node.type === NodeType.Layer || node.type === NodeType.Folder)) {
        return CopyActionValidationStatus.InvalidTypeSelection;
    }

    return CopyActionValidationStatus.Valid;
}

export function getPermissionOnLayerSelection(nodes: Node[], selectedNodeIds: string[]) {
    if (selectedNodeIds.length < 1) {
        return true;
    }
    const selectedNodes = findNodes(nodes, selectedNodeIds, false);
    return canEditAll(selectedNodes);
}
export function createCompositionFeature(feature: PostImportedGeoJsonMapObject, level: number): CompositionMapObject {
    return {
        objectId: feature.id,
        geojson: {
            ...feature.geoJsonFeature,
            properties: {
                ...feature.dataFieldValues,
                id: feature.id,
                layerid: feature.layerId,
                level,
                rotation: feature.geoJsonFeature.properties?.rotation ?? { x: 0, y: 0, z: 0 },
            },
        },
        layerId: feature.layerId,
        level,
        waypoints: undefined,
    };
}

export function createObjectUpdateFromImport(feature: PostImportedGeoJsonMapObject, level: number, sitemaps: Sitemap[], sitemapConfig: MapSitemapConfig[]): ObjectUpdate {
    return {
        objectId: feature.id,
        layerId: feature.layerId,
        geometry: feature.geoJsonFeature.geometry,
        properties: {
            ...feature.dataFieldValues,
            level,
            rotation: feature.geoJsonFeature.properties?.rotation ?? { x: 0, y: 0, z: 0 },
            sitemapId: getMapObjectSitemap(feature.geoJsonFeature.geometry, level, sitemaps, sitemapConfig),
        },
        waypoints: [],
    };
}
